import { useCallback, useState } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import { useQueryParamValue } from 'src/app/shared/hooks/location';

import {
	DESCRIPTION_TEXT_AREA_FOCUS_EVENT,
	DESCRIPTION_TEXT_AREA_PREVIEW_CLOSE_EVENT,
	DESCRIPTION_TEXT_AREA_PREVIEW_FETCH_ERROR_EVENT,
	DESCRIPTION_TEXT_AREA_PREVIEW_FETCH_SUCCESS_EVENT,
	SUGGESTED_TEMPLATE_NOTICE,
} from '../../../../../../constants';
import { useCreatePetitionUpdateContext } from '../../../../context';
import type { UseDescriptionTextAreaResult } from '../../../../types';
import type { EmbeddedMediaPreviewFetchErrorType } from '../../components/EmbeddedMediaPreview/hooks/useEmbeddedMediaPreview';

export function useDescriptionTextArea(): UseDescriptionTextAreaResult {
	const [description, setDescription] = useState<string>('');
	const { translate } = useI18n();
	const track = useTracking();
	const sourceLocation = useQueryParamValue('source_location');
	const isTemplateFilled = useCreatePetitionUpdateContext();

	const onDescriptionChange = useCallback(
		(descriptionInput: string, onChange: (event: string) => void) => {
			onChange(descriptionInput);
			setDescription(descriptionInput);
		},
		[setDescription],
	);

	const trackFocus = useCallback(() => {
		void track(DESCRIPTION_TEXT_AREA_FOCUS_EVENT, {
			petition_update_template: isTemplateFilled ? SUGGESTED_TEMPLATE_NOTICE : undefined,
			source_location: sourceLocation,
			edit: false,
		});
	}, [track, isTemplateFilled, sourceLocation]);

	const onClosePreview = useCallback(() => {
		void track(DESCRIPTION_TEXT_AREA_PREVIEW_CLOSE_EVENT, {
			petition_update_template: isTemplateFilled ? SUGGESTED_TEMPLATE_NOTICE : undefined,
			source_location: sourceLocation,
		});
	}, [track, isTemplateFilled, sourceLocation]);

	const onFetchError = useCallback(
		(errorType: EmbeddedMediaPreviewFetchErrorType) => {
			void track(DESCRIPTION_TEXT_AREA_PREVIEW_FETCH_ERROR_EVENT, {
				petition_update_template: isTemplateFilled ? SUGGESTED_TEMPLATE_NOTICE : undefined,
				source_location: sourceLocation,
				error_type: errorType,
			});
		},
		[track, isTemplateFilled, sourceLocation],
	);

	const onFetchSuccess = useCallback(() => {
		void track(DESCRIPTION_TEXT_AREA_PREVIEW_FETCH_SUCCESS_EVENT, {
			petition_update_template: isTemplateFilled ? SUGGESTED_TEMPLATE_NOTICE : undefined,
			source_location: sourceLocation,
		});
	}, [track, isTemplateFilled, sourceLocation]);

	return {
		data: {
			label: translate('corgi.petition_update.description.label'),
			descriptiveLabel: translate('corgi.petition_update.description.a11y.descriptive_label'),
			description,
		},
		actions: {
			onDescriptionChange,
			trackFocus,
			onClosePreview,
			onFetchError,
			onFetchSuccess,
		},
	};
}
